import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useRef } from 'react';
import {
  Box,
  Divider,
  Flex,
  FlexGrid,
  FlexGridColumn,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  NativeSelect,
  Stack,
  Text,
  Textarea,
  uniqueId,
} from '@hs-baumappe/legacy-ui';
import { FormikCheckboxField, FormikFormField, NameAutocompleteField } from '@hs-baumappe/forms';
import { useTranslation } from 'react-i18next';
import { getIn, useField, useFormikContext } from 'formik';

import { type Employee } from '../../EmployeesSection';
import {
  getSAPEmployeeRoleDefaults,
  OvertimeEmployeeType,
  OvertimeFormValues,
} from '../../../../form';
import { ROLE_TYPE_OPTIONS } from './EmployeeCard.utils';
import { formatNumber } from '../../../../../../utils/number';
import { WorkerRole } from '../../../../../../globalTypes';

interface EmployeeCardProps {
  index: number;
  employees: Employee[];
  employeeNames: string[];
  employeeAutomaticRoleSelection: boolean;
  onRemoveButtonClick?: () => void;
}

type EmployeeField = OvertimeFormValues['employees'][number];

export default function EmployeeCard({
  index,
  employees,
  employeeNames,
  employeeAutomaticRoleSelection,
  onRemoveButtonClick,
}: EmployeeCardProps): JSX.Element {
  const { t } = useTranslation();
  const employeeTitleId = useRef(uniqueId(`employee-${index + 1}-title-`));

  const field = `employees[${index}]`;
  const { setFieldValue } = useFormikContext<OvertimeFormValues>();
  const [employeeField, employeeFieldMeta] = useField<EmployeeField>(field);

  const employee = employeeField.value;
  const employeeErrors = employeeFieldMeta.error;

  // Formik does not provide type safety for errors in nested objects
  // So we have to use getIn helper to be able to access nested object error
  const nameError = getIn(employeeErrors, 'fullName');
  const hoursError = getIn(employeeErrors, 'hours');
  const materialSurchargeError = getIn(employeeErrors, 'materialSurcharge');
  const extraHoursError = getIn(employeeErrors, 'extraHours');
  const machinesUsageError = getIn(employeeErrors, 'machinesUsage');
  const overtimeDetailsError = getIn(employeeErrors, 'overtimeDetails');
  const employeeNotFoundError = getIn(employeeErrors, 'notFound');

  const previousFullNameRef = useRef<string | undefined>(undefined);

  // When name gets cleared, onChange handler is not triggered
  // We have to use effect for setting correct form states
  useEffect(() => {
    const fullNameChanged = previousFullNameRef.current !== employee.fullName;
    previousFullNameRef.current = employee.fullName;

    if (!employee.fullName) {
      // The employee gets reset only when the name is cleared, so that the user can still
      // change the automatic role selection before selecting an employee
      if (!fullNameChanged) {
        return;
      }

      if (employee.type !== OvertimeEmployeeType.DELETED_SAP) {
        setFieldValue(`${field}.employeeId`, '');
      }

      const { automaticRole, role } = getSAPEmployeeRoleDefaults(employeeAutomaticRoleSelection);

      setFieldValue(`${field}.sapRole`, undefined);
      setFieldValue(`${field}.automaticRole`, automaticRole);
      setFieldValue(`${field}.role`, role);
      setFieldValue(`${field}.type`, OvertimeEmployeeType.SAP);
      return;
    }

    const sapEmployee = employees.find(({ name }) => name === employee.fullName);
    if (!sapEmployee) {
      setFieldValue(`${field}.type`, OvertimeEmployeeType.CUSTOM);
      setFieldValue(`${field}.employeeId`, undefined);
      setFieldValue(`${field}.sapRole`, undefined);
      setFieldValue(`${field}.automaticRole`, false);
      setFieldValue(`${field}.role`, employee.role ?? WorkerRole.Fa);
      return;
    }

    setFieldValue(`${field}.type`, OvertimeEmployeeType.SAP);
    setFieldValue(`${field}.employeeId`, sapEmployee.id);
    setFieldValue(`${field}.sapRole`, sapEmployee.role);
    setFieldValue(`${field}.automaticRole`, !employee.role);
  }, [
    employee.fullName,
    employee.role,
    employee.type,
    employees,
    field,
    setFieldValue,
    employeeAutomaticRoleSelection,
  ]);

  function handleAutomaticRoleChange(event: ChangeEvent<HTMLInputElement>) {
    const automaticRole = event.currentTarget.checked;
    const nextRole = !automaticRole ? WorkerRole.Fa : undefined;
    setFieldValue(`${field}.role`, nextRole);
  }

  function handleFieldBlur(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { currentTarget } = event;

    setFieldValue(currentTarget.name, formatNumber(currentTarget.value));
  }

  function handleFieldKeyUp(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { currentTarget } = event;

    currentTarget.value = currentTarget.value.replace('.', ',');
  }

  const sapEmployeeSelected =
    employee.type === OvertimeEmployeeType.SAP ||
    employee.type === OvertimeEmployeeType.DELETED_SAP;

  return (
    <Box
      className={`qa-employee-card-${index}`}
      stylex={{
        backgroundColor: 'white',
        boxShadow: 'medium',
        borderRadius: 'medium',
        padding: 'small-x',
      }}
      role="group"
      aria-labelledby={employeeTitleId.current}
    >
      <Flex alignItems="center">
        <Text id={employeeTitleId.current} variant="title-small">
          {t('employeeForm.employeeNo', { number: index + 1 })}
        </Text>

        {onRemoveButtonClick && (
          <IconButton
            type="button"
            stylex={{ marginLeft: 'auto' }}
            icon={<Icon name="close" />}
            onClick={onRemoveButtonClick}
            aria-label={t('employeeForm.removeButton.label', { number: index + 1 })}
          />
        )}
      </Flex>

      <FlexGrid stylex={{ marginTop: 'small-x' }}>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <NameAutocompleteField
            name={`${field}.fullName`}
            label={t('employeeForm.fullName.label')}
            placeholder={t('employeeForm.fullName.placeholder')}
            names={employeeNames}
            error={employeeNotFoundError || nameError}
          />
        </FlexGridColumn>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          {!sapEmployeeSelected || !employee.automaticRole ? (
            <FormikFormField
              control={NativeSelect}
              name={`${field}.role`}
              label={t('employeeForm.role.label')}
              disabled={sapEmployeeSelected && employee.automaticRole}
            >
              {ROLE_TYPE_OPTIONS.map((roleTypeOption) => (
                <option
                  key={`role-type-option-${roleTypeOption.value}`}
                  value={roleTypeOption.value}
                >
                  {t(roleTypeOption.label)}
                </option>
              ))}
            </FormikFormField>
          ) : (
            <Stack gap="small-5x">
              <FormLabel>{t('employeeForm.role.label')}</FormLabel>
              <Text
                stylex={{ paddingEnds: 'small-3x' }}
                color={!employee.sapRole ? 'muted' : 'text'}
              >
                {employee.sapRole || t('employeeForm.automaticRole.placeholder')}
              </Text>
            </Stack>
          )}
        </FlexGridColumn>
      </FlexGrid>
      <FlexGrid stylex={{ marginTop: 'small-3x' }}>
        <FlexGridColumn sm={{ offset: 6, size: 6 }}>
          <Flex>
            <FormikCheckboxField
              className="qa-employee-automatic-role"
              name={`${field}.automaticRole`}
              label={t('employeeForm.automaticRole.label')}
              variant="filled"
              onChange={handleAutomaticRoleChange}
              disableExtraPressableArea
              disabled={!sapEmployeeSelected}
            />
          </Flex>
        </FlexGridColumn>
      </FlexGrid>

      {employeeNotFoundError && (
        <FormHelperText
          data-testid="employee-not-found-error"
          stylex={{ marginTop: 'small-x' }}
          error
        >
          {employeeNotFoundError}
        </FormHelperText>
      )}

      <Divider stylex={{ marginBottom: 'small-3x', marginTop: 'small-x' }} />

      <FlexGrid>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            name={`${field}.hours`}
            label={t('employeeForm.hours.label')}
            placeholder={t('employeeForm.hours.placeholder')}
            onBlur={handleFieldBlur}
            onKeyUp={handleFieldKeyUp}
            error={hoursError || overtimeDetailsError}
          />
        </FlexGridColumn>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            name={`${field}.extraHours`}
            label={t('employeeForm.extraHours.label')}
            placeholder={t('employeeForm.extraHours.placeholder')}
            onBlur={handleFieldBlur}
            onKeyUp={handleFieldKeyUp}
            error={extraHoursError || overtimeDetailsError}
          />
        </FlexGridColumn>
      </FlexGrid>
      <FlexGrid>
        <FlexGridColumn stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            control={Textarea}
            name={`${field}.materialSurcharge`}
            label={t('employeeForm.materialSurcharge.label')}
            placeholder={t('employeeForm.materialSurcharge.placeholder')}
            error={materialSurchargeError || overtimeDetailsError}
            minRows={3}
            maxRows={6}
          />
        </FlexGridColumn>
        <FlexGridColumn stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            control={Textarea}
            name={`${field}.machinesUsage`}
            label={t('employeeForm.machinesUsage.label')}
            placeholder={t('employeeForm.machinesUsage.placeholder')}
            error={machinesUsageError || overtimeDetailsError}
            minRows={3}
            maxRows={6}
          />
        </FlexGridColumn>
      </FlexGrid>

      {overtimeDetailsError && (
        <FormHelperText
          data-testid="employee-overtime-details-required-error"
          stylex={{ marginTop: 'small-x' }}
          error
        >
          {overtimeDetailsError}
        </FormHelperText>
      )}
    </Box>
  );
}
